import React, { memo, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Nav } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { IoIosArrowBack } from "react-icons/io";
import { FiUser, FiLogOut } from 'react-icons/fi';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { toggleSidebar } from '../application/reducers/ui';
import { setSelectedMenu, setSelectedSubMenu, logout } from '../application/reducers/auth';
import ProfileModal from './ProfileModal';
import './Sidebar.scss';


// Memoized Tooltip Component
const TooltipPositionedExample = memo(({ id, tooltip, element }) => (
    <OverlayTrigger
        key={id}
        placement="right"
        overlay={
            <Tooltip id={`tooltip-${id}`}>
                {tooltip}
            </Tooltip>
        }
    >
        {element}
    </OverlayTrigger>
));

// const profileLinks = [
//     { to: "/profile", icon: "pe-7s-user", name: "Profile" },
//     { to: "/performance", icon: "pe-7s-download", name: "Performance" }
// ];

const Sidebar = () => {
    const ui = useSelector(state => state.ui);
    const { menus, user } = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const isCollapsed = ui.sidebar === 'close';
    const [showProfileModal, setShowProfileModal] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate('/');
    };
    useEffect(() => {
        if (!menus?.length) return;

        const currentUrl = location.pathname;

        // Find current main menu - use find instead of filter for better performance
        const currentMenu = menus.find(menu => currentUrl.startsWith(menu.url));

        if (!currentMenu) {
            dispatch(setSelectedMenu({}));
            dispatch(setSelectedSubMenu({}));
            return;
        }

        dispatch(setSelectedMenu(currentMenu));

        // Handle submenu selection
        if (currentMenu.sub_menus?.length > 0) {
            const currentSubMenu = currentMenu.sub_menus.find(subMenu => {
                const fullSubmenuUrl = `${currentMenu.url}/${subMenu.url}`;
                return currentUrl === fullSubmenuUrl || currentUrl.startsWith(`${fullSubmenuUrl}/`);
            });

            dispatch(setSelectedSubMenu(currentSubMenu || {}));
        } else {
            dispatch(setSelectedSubMenu({}));
        }
    }, [menus, dispatch, location.pathname]);

    const sidebarVariants = {
        open: {
            width: 'var(--sidebar-width)',
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 30
            }
        },
        closed: {
            width: 'var(--sidebar-collapsed-width)',
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 30
            }
        }
    };

    const menuItemVariants = {
        hidden: {
            opacity: 0,
            x: -20
        },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                type: "spring",
                stiffness: 300,
                damping: 25
            }
        }),
        hover: {
            x: 4,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 25
            }
        }
    };

    const iconVariants = {
        normal: {
            scale: 1,
            rotate: 0
        },
        hover: {
            scale: 1.1,
            rotate: 5,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 25
            }
        },
        active: {
            scale: 1.2,
            rotate: 10
        }
    };

    const toggleVariants = {
        open: { rotate: 0 },
        closed: { rotate: 180 }
    };

    const textVariants = {
        visible: {
            opacity: 1,
            width: "auto",
            transition: {
                delay: 0.1,
                duration: 0.3
            }
        },
        hidden: {
            opacity: 0,
            width: 0,
            transition: {
                duration: 0.2
            }
        }
    };

    return (
        <>
        <motion.div
            className={`enhanced-sidebar ${ui.sidebar}`}
            variants={sidebarVariants}
            animate={isCollapsed ? "closed" : "open"}
            initial={false}
        >
            <div className='sidebar-content'>
                <div className='menu-container'>
                    <div className='menu-list'>
                        <Nav defaultActiveKey="/" className="flex-column">
                            {menus.map((link, index) => {
                                const { sub_menus = [], url } = link;
                                const navUrl = sub_menus.length ? `${url}/${sub_menus[0].url}` : url;
                                // const isActive = mainMenuId === link.main_menu_id || location.pathname.includes(url);
                                const pathName = location.pathname === '/' ? '/dashboard' : location.pathname;
                                const isActive = pathName.includes(url);

                                return (
                                    <motion.div
                                        key={link.name}
                                        custom={index}
                                        variants={menuItemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        whileHover="hover"
                                        className={`nav-item-wrapper ${isActive ? 'active' : ''}`}
                                    >
                                        {isCollapsed ? (
                                            <TooltipPositionedExample
                                                element={
                                                    <Nav.Link
                                                        className={`menu-link ${isActive ? 'active' : ''}`}
                                                        as={Link}
                                                        to={navUrl}
                                                        onClick={() => {
                                                            dispatch(setSelectedMenu(link));
                                                            if (sub_menus.length) {
                                                                dispatch(setSelectedSubMenu(sub_menus[0]));
                                                            }
                                                        }}
                                                    >
                                                        <motion.span
                                                            className={`menu-icon pe-7s-${link.icon}`}
                                                            variants={iconVariants}
                                                            whileHover="hover"
                                                            animate={isActive ? "active" : "normal"}
                                                        />
                                                        {isActive && (
                                                            <motion.div
                                                                className="active-indicator"
                                                                layoutId="activeIndicator"
                                                                transition={{
                                                                    type: "spring",
                                                                    stiffness: 300,
                                                                    damping: 30
                                                                }}
                                                            />
                                                        )}
                                                    </Nav.Link>
                                                }
                                                tooltip={link.name}
                                                id={index}
                                            />
                                        ) : (
                                            <Nav.Link
                                                className={`menu-link ${isActive ? 'active' : ''}`}
                                                as={Link}
                                                to={navUrl}
                                                onClick={() => {
                                                    dispatch(setSelectedMenu(link));
                                                    if (sub_menus.length) {
                                                        dispatch(setSelectedSubMenu(sub_menus[0]));
                                                    }
                                                }}
                                            >
                                                <motion.span
                                                    className={`menu-icon pe-7s-${link.icon}`}
                                                    variants={iconVariants}
                                                    whileHover="hover"
                                                    animate={isActive ? "active" : "normal"}
                                                />
                                                <AnimatePresence>
                                                    <motion.span
                                                        className='menu-name'
                                                        variants={textVariants}
                                                        animate="visible"
                                                        exit="hidden"
                                                    >
                                                        {link.name}
                                                    </motion.span>
                                                </AnimatePresence>
                                                {isActive && (
                                                    <motion.div
                                                        className="active-indicator"
                                                        layoutId="activeIndicator"
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 300,
                                                            damping: 30
                                                        }}
                                                    />
                                                )}
                                            </Nav.Link>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </Nav>
                    </div>
                </div>

                {/* ── Bottom: Profile + Sign Out ── */}
                <div className="sidebar-footer">
                    {isCollapsed ? (
                        <>
                            <TooltipPositionedExample
                                id="profile-btn"
                                tooltip="Profile"
                                element={
                                    <button
                                        className="footer-link"
                                        onClick={() => setShowProfileModal(true)}
                                    >
                                        <FiUser size={18} />
                                    </button>
                                }
                            />
                            <TooltipPositionedExample
                                id="signout-btn"
                                tooltip="Sign Out"
                                element={
                                    <button className="footer-link footer-signout" onClick={handleLogout}>
                                        <FiLogOut size={18} />
                                    </button>
                                }
                            />
                        </>
                    ) : (
                        <>
                            <button
                                className="footer-link"
                                onClick={() => setShowProfileModal(true)}
                            >
                                <FiUser size={18} />
                                <span className="footer-label">Profile</span>
                            </button>
                            <button className="footer-link footer-signout" onClick={handleLogout}>
                                <FiLogOut size={18} />
                                <span className="footer-label">Sign Out</span>
                            </button>
                        </>
                    )}
                </div>

            </div>

            {isCollapsed ? (
                <TooltipPositionedExample
                    element={
                        <motion.div
                            role="button"
                            tabIndex={0}
                            aria-label="Expand sidebar"
                            className='sidebar-toggle'
                            onClick={() => dispatch(toggleSidebar())}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && dispatch(toggleSidebar())}
                            whileHover={{
                                scale: 1.1,
                                y: "-50%"
                            }}
                            whileTap={{
                                scale: 0.95,
                                y: "-50%"
                            }}
                            variants={toggleVariants}
                            animate={isCollapsed ? "closed" : "open"}
                            initial={false}
                            layout
                        >
                            <motion.span
                                className='toggle-icon'
                                animate={{
                                    rotate: isCollapsed ? 180 : 0,
                                    scale: isCollapsed ? 0.9 : 1
                                }}
                                transition={{
                                    type: "spring",
                                    stiffness: 400,
                                    damping: 30,
                                    duration: 0.3
                                }}
                                whileHover={{
                                    scale: 1.2,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <IoIosArrowBack size={16} />
                            </motion.span>

                            <motion.div
                                className="toggle-indicator"
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{
                                    opacity: isCollapsed ? 1 : 0,
                                    scale: isCollapsed ? 1 : 0
                                }}
                                transition={{
                                    delay: isCollapsed ? 0.2 : 0,
                                    duration: 0.2
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    width: '8px',
                                    height: '8px',
                                    background: 'var(--accent, #ff6b6b)',
                                    borderRadius: '50%',
                                    border: '2px solid var(--white)'
                                }}
                            />
                        </motion.div>
                    }
                    tooltip="Expand Sidebar"
                    id="sidebar-toggle"
                />
            ) : (
                <motion.div
                    role="button"
                    tabIndex={0}
                    aria-label="Collapse sidebar"
                    className='sidebar-toggle'
                    onClick={() => dispatch(toggleSidebar())}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && dispatch(toggleSidebar())}
                    whileHover={{
                        scale: 1.1,
                        y: "-50%"
                    }}
                    whileTap={{
                        scale: 0.95,
                        y: "-50%"
                    }}
                    variants={toggleVariants}
                    animate={isCollapsed ? "closed" : "open"}
                    initial={false}
                    layout
                >
                    <motion.span
                        className='toggle-icon'
                        animate={{
                            rotate: isCollapsed ? 180 : 0,
                            scale: isCollapsed ? 0.9 : 1
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 30,
                            duration: 0.3
                        }}
                        whileHover={{
                            scale: 1.2,
                            transition: { duration: 0.2 }
                        }}
                    >
                        <IoIosArrowBack size={16} />
                    </motion.span>

                    <motion.div
                        className="toggle-indicator"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: isCollapsed ? 1 : 0,
                            scale: isCollapsed ? 1 : 0
                        }}
                        transition={{
                            delay: isCollapsed ? 0.2 : 0,
                            duration: 0.2
                        }}
                        style={{
                            position: 'absolute',
                            top: '-2px',
                            right: '-2px',
                            width: '8px',
                            height: '8px',
                            background: 'var(--accent, #ff6b6b)',
                            borderRadius: '50%',
                            border: '2px solid var(--white)'
                        }}
                    />
                </motion.div>
            )}
        </motion.div>
        <ProfileModal show={showProfileModal} onHide={() => setShowProfileModal(false)} />
        </>
    );
};

export default memo(Sidebar);
